/**
 * Shared formatting/labelling helpers for the Alerts admin surface
 * (`alerts-admin.html` / `alert-severity-pills.html`, Slice A4). Mirrors
 * `serverFormat.js`'s plain-function shape (no Vue/Pinia dependency).
 */

import { formatRelativeTime } from '@root/services/serverFormat';

const SEVERITY_META = {
  info: { label: 'Info', icon: 'info' },
  warning: { label: 'Warning', icon: 'warning' },
  serious: { label: 'Serious', icon: 'warning' },
  critical: { label: 'Critical', icon: 'warning' },
};

const STATUS_META = {
  open: { label: 'Open' },
  acknowledged: { label: 'Acknowledged' },
  resolved: { label: 'Resolved', icon: 'check' },
};

export function severityMeta(severity) {
  return SEVERITY_META[severity] || { label: severity || 'Unknown', icon: 'info' };
}

export function statusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unknown' };
}

/**
 * `AlertRule::$config`/`$channel_ids`/`$server_scope_config` can arrive as
 * raw JSON-encoded strings rather than decoded objects/arrays — same
 * `getData()` quirk `serverFormat.js`'s `parseIps()` documents for
 * `Server::$ips`. Defensive parse so a template never iterates a string's
 * characters or renders `[object Object]`.
 */
export function parseMaybeJson(value, fallback) {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value !== 'string') {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

const RULE_TYPE_LABELS = {
  metric_threshold: 'Metric threshold',
  check_state: 'Check-state change',
  heartbeat_missed: 'Heartbeat missed',
  domain_expiration: 'Domain expiring',
  security_audit: 'Security audit',
  intrusion: 'Intrusion detection',
  integration_stale: 'Integration stale',
  inventory_stale: 'Inventory stale',
  hub_unreachable: 'Hub unreachable',
  fleet_dark: 'Entire fleet dark',
  agent_cert_expiring: 'Agent certificate expiring',
  agent_cert_rejected: 'Agent certificate rejected',
  agent_cert_grace_renewed: 'Agent certificate grace-renewed',
  site_tls_expiring: 'Site TLS certificate expiring',
};

export function ruleTypeLabel(type) {
  return RULE_TYPE_LABELS[type] || type || '';
}

// Plan 012 §4 — the three delivery modes. Since Phase 1 `digest` means the
// channel's daily digest (and, for serious items on Google Chat, the hourly
// roll-up); escalations can still interrupt.
const DELIVERY_MODE_META = {
  immediate: { label: 'Immediate', hint: 'Immediate: one line when the alert opens, repeats by severity in its thread, and a resolve notice.' },
  digest: { label: 'Digest', hint: 'Digest: no message of its own — it is listed in each channel’s daily digest until it resolves (serious items also in the hourly roll-up). A tighter expiry band, a new critical check or "escalate after" still interrupts.' },
  dashboard: { label: 'Dashboard only', hint: 'Dashboard only: never sent — the alert shows in the feed and on the Overview only.' },
};

export function deliveryModeLabel(mode) {
  return DELIVERY_MODE_META[mode]?.label || mode || 'Immediate';
}

export function deliveryModeHint(mode) {
  return DELIVERY_MODE_META[mode]?.hint || '';
}

export function ruleTriggerSummary(rule) {
  const config = parseMaybeJson(rule.config, {});
  switch (rule.type) {
    case 'metric_threshold':
      return `metric threshold · ${config.metric || '?'} ${config.operator || '>'} ${config.value ?? '?'}`;
    case 'check_state':
      return `check-state · ${config.consecutive || 1} consecutive`;
    case 'heartbeat_missed':
      return `heartbeat missed · ${config.miss_threshold || 3} misses`;
    case 'intrusion': {
      const ids = Array.isArray(config.detection_rule_ids) ? config.detection_rule_ids : [];
      return ids.length ? `intrusion · ${ids.join(', ')}` : 'intrusion · all detectors';
    }
    default:
      return ruleTypeLabel(rule.type).toLowerCase();
  }
}

// ── Alerts workspace (Plan 012 Phase 2, design brief §3–§4) ──────────────

// `AlertRuleType::shortTitle()` — the feed uses the chat's short titles so
// the feed and the chat read the same (brief §4.5); the longer labels above
// stay for the rules table.
const RULE_TYPE_SHORT = {
  metric_threshold: 'Metric threshold',
  check_state: 'Service failing',
  heartbeat_missed: 'Heartbeat missed',
  domain_expiration: 'Domain expiring',
  security_audit: 'Security audit',
  intrusion: 'Intrusion',
  integration_stale: 'Integration stale',
  inventory_stale: 'Inventory stale',
  hub_unreachable: 'Hub unreachable',
  fleet_dark: 'Entire fleet dark',
  agent_cert_expiring: 'Agent cert expiring',
  agent_cert_rejected: 'Agent cert rejected',
  agent_cert_grace_renewed: 'Grace renewal used',
  site_tls_expiring: 'TLS cert expiring',
};

export const RULE_TYPES = Object.keys(RULE_TYPE_SHORT);

export function ruleTypeShortTitle(type) {
  return RULE_TYPE_SHORT[type] || ruleTypeLabel(type);
}

/** "2h 10m" / "45m" / "3d 4h" / "40s" — the chat's duration shape. */
export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 && d < 7 ? `${d}d ${h % 24}h` : `${d}d`;
}

/** "Wed 21 Oct 09:00" — a moment within the next weeks. */
export function formatMoment(epoch) {
  if (!epoch) return '—';
  const date = new Date(Number(epoch) * 1000);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** "1 Jan 2027" */
export function formatDay(epoch) {
  if (!epoch) return '—';
  const date = new Date(Number(epoch) * 1000);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "in 79 days" / "today" / "1 day ago" — relative time over absolute (brief §5). */
export function relativeDays(epoch, nowSeconds = Math.floor(Date.now() / 1000)) {
  if (!epoch) return '';
  const days = Math.round((Number(epoch) - nowSeconds) / 86400);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${-days} day${days === -1 ? '' : 's'} ago`;
}

/** `YYYY-MM-DD` for a date input, from epoch seconds. */
export function dateInputValue(epoch) {
  const date = new Date(Number(epoch) * 1000);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A date input's `YYYY-MM-DD` → epoch seconds at 09:00 local that day (null when blank/invalid). */
export function epochFromDateInput(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, mo, d] = value.split('-').map(Number);
  const date = new Date(y, mo - 1, d, 9, 0, 0);
  return Number.isNaN(date.getTime()) ? null : Math.floor(date.getTime() / 1000);
}

/**
 * The delivery chip (brief §3.3) from the feed row's `delivery` block and
 * `notify_outcome`. Returns null when there is nothing honest to say.
 * `kind`: 'delivery' (outlined, neutral) · 'fail' (the one danger chip).
 */
export function deliveryChip(row, nowSeconds = Math.floor(Date.now() / 1000)) {
  const d = row.delivery || {};
  const outcome = d.outcome ?? row.notify_outcome ?? null;
  const sentTo = Array.isArray(d.sent_to) ? d.sent_to : [];
  if (sentTo.length) {
    const text = sentTo.length === 1 ? `sent to ${sentTo[0]}` : `sent to ${sentTo.length} channels`;
    let muted = '';
    if (row.status === 'acknowledged') {
      muted = 'repeats stopped';
    } else if (row.status === 'open' && d.repeat_due_at) {
      muted = `repeat in ${formatDuration(Math.max(0, d.repeat_due_at - nowSeconds))}`;
    }
    return { kind: 'delivery', icon: 'chat', text, muted, title: sentTo.join(', ') };
  }
  switch (outcome) {
    case 'delivered':
      return { kind: 'delivery', icon: 'chat', text: 'sent', muted: row.status === 'acknowledged' ? 'repeats stopped' : '', title: 'Sent before the delivery ledger existed' };
    case 'digest':
      return { kind: 'delivery', icon: 'digest', text: 'in digest', muted: '', title: (d.digest_on || []).join(', ') };
    case 'pending_delay':
      return { kind: 'delivery', icon: 'clock', text: 'waiting to send', muted: '', title: 'Sends when its delay has passed, if still open' };
    case 'dashboard_only':
      return { kind: 'delivery', icon: null, text: 'dashboard only', muted: '', title: '' };
    case 'below_floor':
      return { kind: 'delivery', icon: null, text: 'below channel floor', muted: '', title: '' };
    case 'no_channels':
      return { kind: 'delivery', icon: null, text: 'no channel', muted: '', title: 'Attach a channel under Rules & channels', link: 'rules' };
    case 'all_failed': {
      const first = (d.failed_on || [])[0];
      return { kind: 'fail', icon: null, text: 'send failed', muted: '', title: first?.error ? `${first.channel}: ${first.error}` : '' };
    }
    default:
      return null;
  }
}

export { formatRelativeTime };
